import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, Barcode, Banknote, CreditCard, Eraser, History, Image as ImageIcon,
  Landmark, Loader2, Minus, PackagePlus, Percent, Plus, Printer, Search,
  ShoppingBasket, Store, Trash2, UserRound,
} from "lucide-react";
import PriceHistoryDialog from "@/components/PriceHistoryDialog";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiError } from "@/lib/api";
import { fetchAvailability } from "@/lib/stockAvailability";
import { formatVariantLabel, sortVariants } from "@/lib/variants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { matchesAll } from "@/lib/trFold";
import BarcodeScanner from "@/components/BarcodeScanner";
import { lookupBarcode, UnknownBarcodeDialog } from "@/components/barcode/BarcodeFlow";

const money = (value) => `₺${Number(value || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const qtyFmt = (value) => Number(value || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 });
const slug = (value) => String(value || "").toLocaleLowerCase("tr-TR").replace(/[^a-z0-9ğüşöçıi]+/gi, "-");
const trNormalize = (value) => String(value || "")
  .toLocaleLowerCase("tr-TR")
  .replaceAll("ı", "i").replaceAll("ş", "s").replaceAll("ğ", "g")
  .replaceAll("ü", "u").replaceAll("ö", "o").replaceAll("ç", "c");

const WALK_IN_CUSTOMER_NAMES = ["nihai tüketici", "perakende müşteri"];

const PAYMENT_METHODS = [
  { id: "card", label: "Kredi Kartı", icon: CreditCard, code: "kart" },
  { id: "cash", label: "Nakit", icon: Banknote, code: "nakit" },
  { id: "transfer", label: "Havale / EFT", icon: Landmark, code: "havale" },
];

// Hızlı Satış deposu: yalnız MAĞAZA (backend /quick-sale/store-warehouse). Tek istek, cache'li.
let storeWarehousePromise = null;
const getStoreWarehouse = () => {
  if (!storeWarehousePromise) {
    storeWarehousePromise = api.get("/quick-sale/store-warehouse").then(({ data }) => data).catch(() => {
      storeWarehousePromise = null;
      return null;
    });
  }
  return storeWarehousePromise;
};

const stockFor = (rows, productId, variantId = "", store = null) => {
  if (!rows) return null;
  const matched = rows.filter((row) => row.product_id === productId && (row.variant_id || "") === (variantId || ""));
  // Sadece MAĞAZA fiziksel kullanılabilir (fiziksel − rezerve); yoldaki/başka depo satışta KULLANILMAZ.
  const own = store ? matched.find((row) => row.warehouse_id === store.id) : null;
  const otherAvailable = matched
    .filter((row) => row.warehouse_id && row.warehouse_id !== store?.id)
    .reduce((sum, row) => sum + Number(row.available_physical ?? 0), 0);
  return {
    available: store ? Number(own?.available_physical ?? 0) : 0,
    otherAvailable,
    warehouseId: store?.id || "",
    warehouseName: store?.name || "",
  };
};

const CURRENCY_SYMBOLS = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };
const fmtSource = (amount, currency) => {
  const value = Number(amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${CURRENCY_SYMBOLS[currency] || ""}${value} ${currency || ""}`.trim();
};

// Ürün kartı SATIŞ KDV % (alış KDV kullanılmaz); %0 geçerli. Alan hiç yoksa ürün modeli varsayılanı (%10).
const saleTaxRate = (product) => {
  const raw = product?.sale_tax_rate;
  const n = Number(raw);
  return raw === null || raw === undefined || raw === "" || !Number.isFinite(n) ? 10 : n;
};


const toSaleOption = (product, variant, stockRows, imageMap, store) => {
  const variantId = variant?.id || "";
  const sourcePrice = Number(variant?.sales_price || product.sale_price || product.base_price || 0);
  const sourceCurrency = String(product.sale_currency || "TRY").toUpperCase();
  const stock = stockFor(stockRows, product.id, variantId, store);
  return {
    key: `${product.id}:${variantId}`,
    id: variantId || product.id,
    productId: product.id,
    variantId,
    name: product.name,
    variant: variant ? formatVariantLabel(variant.attributes, variant.sku || "Standart") : "Standart",
    sku: variant?.sku || product.sku || product.product_code || "—",
    barcode: variant?.barcode || product.barcode || "",
    category: product.category || "Diğer",
    sourcePrice,
    sourceCurrency,
    taxRate: saleTaxRate(product),
    available: stock?.available ?? 0,
    otherAvailable: stock?.otherAvailable ?? 0,
    warehouseId: stock?.warehouseId || "",
    warehouseName: stock?.warehouseName || "",
    image: imageMap[product.id] || "",
  };
};

const ProductImage = ({ src, alt, className }) => (
  src ? (
    <img src={src} alt={alt} className={className} loading="lazy" />
  ) : (
    <div className={`${className} flex items-center justify-center bg-slate-100 text-slate-300`}>
      <ImageIcon className="h-6 w-6" />
    </div>
  )
);

export default function QuickSale() {
  const { can } = useAuth();
  const canOverridePrice = can("orders", "edit");
  const [cart, setCart] = useState([]);
  const [scanOpen, setScanOpen] = useState(false);
  const [unknownCode, setUnknownCode] = useState("");
  const [categories, setCategories] = useState(["Tümü"]);
  const [category, setCategory] = useState("Tümü");
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerOpen, setCustomerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [quickItems, setQuickItems] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [imageByProduct, setImageByProduct] = useState({});
  const [companyInfo, setCompanyInfo] = useState(null);
  const [lastSale, setLastSale] = useState(null);
  const [fxRates, setFxRates] = useState({ TRY: 1 });
  const [historyOpenFor, setHistoryOpenFor] = useState(null);

  // Mevcut TCMB kur altyapısı reuse — kayıt anında satış hâlâ TRY olarak yazılır.
  const ensureFxRate = async (currency) => {
    const code = String(currency || "TRY").toUpperCase();
    if (code === "TRY") return 1;
    if (fxRates[code]) return fxRates[code];
    try {
      const { data } = await api.get("/currency/rate", { params: { currency: code, rate_type: "forex_selling" } });
      const rate = Number(data?.rate || 0);
      if (rate > 0) {
        setFxRates((current) => ({ ...current, [code]: rate }));
        return rate;
      }
    } catch { /* kur alınamadıysa güvenli fallback yapılmaz — kullanıcıya bildirilir */ }
    return null;
  };

  const loadImages = async (products) => {
    const missing = (products || []).filter((product) => product?.id && imageByProduct[product.id] === undefined);
    if (!missing.length) return {};
    const entries = await Promise.all(missing.map(async (product) => {
      try {
        const { data } = await api.get(`/products/${product.id}/images`);
        const image = (data || []).find((item) => item.is_primary) || (data || [])[0];
        return [product.id, image ? `${API}/products/${product.id}/images/${image.id}/download` : ""];
      } catch {
        return [product.id, ""];
      }
    }));
    const next = Object.fromEntries(entries);
    setImageByProduct((current) => ({ ...current, ...next }));
    return next;
  };

  const loadVariants = async (productId) => {
    try {
      const { data } = await api.get(`/products/${productId}/variants`);
      return sortVariants(data || []);
    } catch {
      return [];
    }
  };

  useEffect(() => {
    let active = true;
    const loadProducts = async () => {
      setLoadingProducts(true);
      try {
        const { data: products } = await api.get("/products", { params: { limit: 100 } });
        const productList = Array.isArray(products) ? products : [];
        const priced = productList.filter((product) => Number(product.sale_price || product.base_price || 0) > 0);
        const stocked = priced.filter((product) => Number(product.stock_total ?? 0) > 0);
        const quickSource = (stocked.length ? stocked : priced).slice(0, 12);
        const productIds = quickSource.map((product) => product.id).filter(Boolean);
        const store = await getStoreWarehouse();
        if (!store) toast.error("MAĞAZA deposu tanımlı değil — Hızlı Satış stok düşemez");
        const stockRows = productIds.length ? await fetchAvailability({ productIds }) : [];
        const imageMap = await loadImages(quickSource);
        const variantLists = await Promise.all(quickSource.map((product) => loadVariants(product.id)));
        const items = [];
        quickSource.forEach((product, index) => {
          const variants = variantLists[index] || [];
          if (variants.length) {
            variants.slice(0, 3).forEach((variant) => items.push(toSaleOption(product, variant, stockRows, imageMap, store)));
          } else {
            items.push(toSaleOption(product, null, stockRows, imageMap, store));
          }
        });
        if (!active) return;
        setQuickItems(
          items
            .sort((a, b) => (b.available ?? -1) - (a.available ?? -1))
            .slice(0, 8),
        );
        setCategories(["Tümü", ...new Set(quickSource.map((product) => product.category).filter(Boolean))]);
      } catch (error) {
        if (active) toast.error(formatApiError(error));
      } finally {
        if (active) setLoadingProducts(false);
      }
    };
    loadProducts();
    return () => { active = false; };
    // İlk yükleme tek seferlik; image map sonraki aramalar için cache'lenir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let active = true;
    const loadCustomers = async () => {
      try {
        const { data } = await api.get("/customers");
        if (!active) return;
        const list = Array.isArray(data) ? data : [];
        setCustomers(list);
        const walkIn = list.find((c) => WALK_IN_CUSTOMER_NAMES.includes((c.name || "").trim().toLocaleLowerCase("tr-TR")));
        setCustomerId((current) => current || (walkIn?.id ?? ""));
        if (walkIn) setCustomerQuery((current) => current || walkIn.name);
      } catch {
        if (active) setCustomers([]);
      }
    };
    loadCustomers();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const loadCompanyInfo = async () => {
      // Mevcut şirket/tenant ayarları: belge şablonu (company_name/address/phone/tax)
      // — settings yetkisi yoksa tenant adına (auth/me) düş. Alan yoksa fişte gösterilmez.
      try {
        const { data } = await api.get("/document-templates/invoice");
        if (active) setCompanyInfo({
          name: data.company_name || "",
          address: data.company_address || "",
          phone: data.company_phone || "",
          taxOffice: data.company_tax_office || "",
          taxNo: data.company_tax_no || "",
        });
        return;
      } catch { /* settings yetkisi yoksa tenant adıyla devam */ }
      try {
        const { data } = await api.get("/auth/me");
        if (active) setCompanyInfo({ name: data.company_name || "" });
      } catch { if (active) setCompanyInfo({}); }
    };
    loadCompanyInfo();
    return () => { active = false; };
  }, []);

  const filteredCustomers = useMemo(() => {
    const q = trNormalize(customerQuery.trim());
    if (!q) return customers;
    return customers.filter((c) => matchesAll(q, [c.name, c.contact_person, c.phone, c.tax_no]));
  }, [customers, customerQuery]);

  // Yazarken otomatik ürün araması (debounce) — barkod okuyucu Enter ile
  // hâlâ anında otomatik ekler (searchProducts(search, true)).
  useEffect(() => {
    const q = search.trim();
    if (!q) {
      setSearchResults([]);
      setShowResults(false);
      return undefined;
    }
    const timer = setTimeout(() => { searchProducts(q, false); }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const visibleQuickItems = useMemo(
    () => quickItems.filter((item) => category === "Tümü" || item.category === category),
    [category, quickItems],
  );

  const totals = useMemo(() => {
    // KDV satır bazında: her satır ürün kartındaki SATIŞ KDV % (sabit oran yok)
    const r2 = (n) => Math.round(n * 100) / 100;
    let subtotal = 0; let discount = 0; let vat = 0;
    const rates = new Set();
    cart.forEach((item) => {
      const gross = item.unitPrice * item.quantity;
      const disc = (gross * item.discount) / 100;
      subtotal += gross; discount += disc;
      vat += r2((Math.max(0, gross - disc) * (Number(item.taxRate) || 0)) / 100);
      rates.add(Number(item.taxRate) || 0);
    });
    const taxable = Math.max(0, subtotal - discount);
    vat = r2(vat);
    return { subtotal, discount, vat, total: r2(taxable + vat), rates: [...rates].sort((a, b) => a - b) };
  }, [cart]);

  const addProduct = async (product) => {
    const existing = cart.find((item) => item.key === product.key);
    const nextQuantity = (existing?.quantity || 0) + 1;
    if (product.available !== null && nextQuantity > product.available) {
      toast.warning(`Yetersiz stok: ${product.name} — ${product.warehouseName || "MAĞAZA"} kullanılabilir ${qtyFmt(product.available)} adet`
        + (product.otherAvailable > 0 ? ` (diğer depolarda ${qtyFmt(product.otherAvailable)} adet var — Depolar Arası Transfer gerekli)` : ""));
      return;
    }
    if (existing) {
      setCart((current) => current.map((item) => item.key === product.key ? { ...item, quantity: item.quantity + 1 } : item));
      toast.success(`${product.name} sepete eklendi`);
      return;
    }
    // TL karşılığı: mevcut TCMB kur altyapısı reuse — dövizli üründe fx * source
    let tryPrice = product.sourcePrice;
    let fxRate = 1;
    if (product.sourceCurrency !== "TRY") {
      const rate = await ensureFxRate(product.sourceCurrency);
      if (!rate) {
        toast.error(`${product.sourceCurrency} için TCMB kuru alınamadı — ürün eklenemedi.`);
        return;
      }
      fxRate = rate;
      tryPrice = Number((product.sourcePrice * rate).toFixed(2));
    }
    setCart((current) => [...current, {
      ...product, quantity: 1, discount: 0,
      unitPrice: tryPrice, tryPrice, fxRate, priceEdited: false,
    }]);
    toast.success(`${product.name} sepete eklendi`);
  };

  const updateQuantity = (key, delta) => {
    const item = cart.find((row) => row.key === key);
    if (!item) return;
    const nextQuantity = Math.max(1, item.quantity + delta);
    if (item.available !== null && nextQuantity > item.available) {
      toast.warning(`Yetersiz stok: ${item.warehouseName || "MAĞAZA"} kullanılabilir ${qtyFmt(item.available)} adet`
        + (item.otherAvailable > 0 ? ` (diğer depolarda ${qtyFmt(item.otherAvailable)} adet — transfer gerekli)` : ""));
      return;
    }
    setCart((current) => current.map((row) => row.key === key ? { ...row, quantity: nextQuantity } : row));
  };

  const updateDiscount = (key, value) => {
    const parsed = Number(value);
    setCart((current) => current.map((item) => (
      item.key === key ? { ...item, discount: Math.min(100, Math.max(0, Number.isFinite(parsed) ? parsed : 0)) } : item
    )));
  };

  const updateUnitPrice = (key, value) => {
    const parsed = Number(String(value).replace(",", "."));
    setCart((current) => current.map((item) => (
      item.key === key
        ? { ...item, unitPrice: Number.isFinite(parsed) && parsed >= 0 ? parsed : 0, priceEdited: true }
        : item
    )));
  };

  const searchProducts = async (term, autoAdd = false) => {
    const query = String(term || "").trim();
    if (!query) return;
    setSearching(true);
    try {
      const { data } = await api.get("/products/search", { params: { q: query, limit: 8 } });
      const results = Array.isArray(data) ? data : [];
      if (!results.length) {
        setSearchResults([]);
        setShowResults(true);
        if (autoAdd) toast.info("Ürün veya barkod bulunamadı");
        return;
      }
      const products = [];
      const productIds = [];
      results.forEach((result) => {
        if (result.product?.id && !productIds.includes(result.product.id)) {
          products.push(result.product);
          productIds.push(result.product.id);
        }
      });
      const store = await getStoreWarehouse();
      const stockRows = await fetchAvailability({ productIds });
      const imageMap = { ...imageByProduct, ...(await loadImages(products)) };
      const variantLists = await Promise.all(results.map((result) => (
        result.variant ? Promise.resolve([]) : loadVariants(result.product.id)
      )));
      const options = [];
      results.forEach((result, index) => {
        if (result.variant) {
          options.push(toSaleOption(result.product, result.variant, stockRows, imageMap, store));
          return;
        }
        const variants = variantLists[index] || [];
        if (variants.length) {
          variants.slice(0, 4).forEach((variant) => options.push(toSaleOption(result.product, variant, stockRows, imageMap, store)));
        } else {
          options.push(toSaleOption(result.product, null, stockRows, imageMap, store));
        }
      });
      setSearchResults(options);
      setShowResults(true);

      const exactBarcode = results.find((result) => result.match_reason === "barcode");
      if (autoAdd && exactBarcode) {
        const barcodeOption = toSaleOption(exactBarcode.product, exactBarcode.variant, stockRows, imageMap, store);
        await addProduct(barcodeOption);
        setSearch("");
        setShowResults(false);
      } else if (autoAdd && options.length === 1) {
        await addProduct(options[0]);
        setSearch("");
        setShowResults(false);
      } else if (autoAdd) {
        toast.info("Birden fazla sonuç bulundu; listeden ürün/varyant seçin");
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSearching(false);
    }
  };

  const pickSearchResult = (product) => {
    addProduct(product);
    setSearch("");
    setShowResults(false);
  };

  // Ortak barkod akışı: tam eşleşme → mevcut arama/sepete ekleme yolu (aynı varyant +1)
  const onScanned = async (code) => {
    try {
      const r = await lookupBarcode(code);
      if (r.found) await searchProducts(r.code, true);
      else setUnknownCode(r.code);
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const applyHistoricPrice = (key, row) => {
    // "Fiyatı Kullan" — TL'ye çevirerek manuel birim fiyata yansıt.
    const trySource = row.try_unit_price ?? (String(row.currency).toUpperCase() === "TRY" ? Number(row.unit_price || 0) : null);
    if (trySource == null) {
      toast.error("Bu fiyat için TL karşılığı yok, kullanılamaz.");
      return;
    }
    setCart((current) => current.map((item) => (
      item.key === key ? { ...item, unitPrice: Number(trySource), priceEdited: true } : item
    )));
    setHistoryOpenFor(null);
    toast.success("Önceki fiyat birim fiyata uygulandı");
  };

  const completeSale = async () => {
    if (submitting) return;
    if (!cart.length) {
      toast.warning("Sepet boş");
      return;
    }
    if (!customerId) {
      toast.warning("Satış tamamlamadan önce müşteri seçin");
      return;
    }
    const invalid = cart.find((item) => !item.warehouseId || item.available === null || item.quantity > item.available);
    if (invalid) {
      toast.error(!invalid.warehouseId
        ? "MAĞAZA deposu tanımlı değil — satış tamamlanamaz"
        : `Stok yetersiz: ${invalid.name} — ${invalid.warehouseName} kullanılabilir ${qtyFmt(invalid.available)} adet`);
      return;
    }
    setSubmitting(true);
    let createdOrderId = "";
    let createdOrderNo = "";
    const deducted = [];
    try {
      const nowIso = new Date().toISOString();
      const lineItems = cart.map((item) => ({
        id: item.id,
        product_id: item.productId,
        product_name: item.name,
        variant_id: item.variantId || "",
        variant_sku: item.sku,
        variant_attributes: item.variant === "Standart" ? {} : { label: item.variant },
        quantity: item.quantity,
        unit_price: item.unitPrice,
        currency: "TRY",
        tax_rate: item.taxRate,
        discount_percent: item.discount,
        discount_amount: 0,
        warehouse_id: item.warehouseId,
        warehouse_name: item.warehouseName,
        applications: [],
      }));
      const { data: order } = await api.post("/orders/wizard", {
        customer_id: customerId,
        create_as: "order",
        line_items: lineItems,
        notes: `Hızlı Satış · ${nowIso}`,
      });
      createdOrderId = order?.id || "";
      createdOrderNo = order?.order_no || "";
      if (!createdOrderId) throw new Error("Satış kaydı oluşturulamadı");

      // Backend kuralı: yalnız MAĞAZA, (fiziksel − rezerve) ≥ miktar, atomik; başka depoya fallback yok.
      await api.post("/quick-sale/stock-deduct", {
        order_id: createdOrderId,
        items: cart.map((item) => ({ product_id: item.productId, variant_id: item.variantId || "", quantity: item.quantity })),
      });
      deducted.push(...cart);

      await api.patch(`/orders/${createdOrderId}/stage`, { stage: "tamamlandi" });

      // Mevcut cari tahsilat altyapısı (finance.py) — yeni ödeme sistemi YOK.
      // order_id ile satışa bağlanır, paid_amount otomatik güncellenir,
      // idempotency_key çift tahsilatı engeller. Tahsilat hatası outer catch'e
      // düşer → stok iade + sipariş iptal (yarım işlem bırakılmaz), sepet korunur.
      const method = PAYMENT_METHODS.find((m) => m.id === paymentMethod) || PAYMENT_METHODS[0];
      await api.post(`/customers/${customerId}/payments`, {
        order_id: createdOrderId,
        amount: Number(order?.total_price || totals.total),
        method: method.code,
        notes: `Hızlı Satış ${createdOrderNo} · ${method.label}`,
        idempotency_key: `pos-${createdOrderId}`,
      });
      toast.success(`Satış tamamlandı · Fiş No: ${createdOrderNo} · Tahsilat: ${method.label}`);
      // Fiş için satış snapshot'ı (sepet temizlenmeden ÖNCE alınır)
      const customerName = customers.find((c) => c.id === customerId)?.name || "";
      setLastSale({
        orderNo: createdOrderNo,
        date: nowIso,
        customerName,
        paymentLabel: method.label,
        items: cart.map((item) => ({
          name: item.name,
          variant: item.variant,
          quantity: item.quantity,
          price: item.unitPrice,
          discount: item.discount,
          lineTotal: item.unitPrice * item.quantity * (1 - item.discount / 100),
        })),
        totals: { ...totals },
      });
      setCart([]);
    } catch (error) {
      if (deducted.length && createdOrderId) {
        // Yalnız bu satışta düşülen adetler, bir kez geri eklenir (backend kaydından)
        try { await api.post("/quick-sale/stock-restore", { order_id: createdOrderId }); } catch { /* compensasyon hatası loglanır */ }
      }
      if (createdOrderId) {
        try { await api.patch(`/orders/${createdOrderId}/stage`, { stage: "iptal" }); } catch { /* no-op */ }
      }
      toast.error(formatApiError(error) || "Satış tamamlanamadı");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-100 px-4 py-5 pb-24 text-slate-900 md:px-6" data-testid="quick-sale-page">
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              <Store className="h-4 w-4" /> Mağaza · Hızlı Satış
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-950">Hızlı Satış</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-950" data-testid="shift-summary-button">
              Vardiya Özeti
            </Button>
            <Button
              variant="outline"
              className="border-rose-200 bg-white text-rose-600 hover:bg-rose-50 hover:text-rose-700"
              onClick={() => setCart([])}
              data-testid="clear-cart-button"
            >
              <Eraser className="mr-2 h-4 w-4" /> Sepeti Temizle
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 2xl:grid-cols-12">
          <div className="space-y-5 2xl:col-span-8">
            <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="barcode-search-panel">
              <div className="flex flex-col gap-3 md:flex-row">
                <div className="relative flex-1">
                  <Barcode className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        searchProducts(search, true);
                      }
                    }}
                    placeholder="Barkod okut veya ürün ara..."
                    autoComplete="off"
                    className="h-12 rounded-lg border-slate-300 bg-slate-50 pl-12 text-base text-slate-900 placeholder:text-slate-400 focus-visible:ring-blue-500"
                    data-testid="barcode-input"
                  />
                </div>
                <Button className="h-12 rounded-lg bg-blue-600 px-6 text-white hover:bg-blue-700" onClick={() => searchProducts(search, true)} disabled={searching} data-testid="product-search-button">
                  {searching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />} Ürün Ara
                </Button>
                <Button className="h-12 rounded-lg bg-slate-900 px-6 text-white hover:bg-slate-800" onClick={() => setScanOpen(true)} data-testid="quick-sale-scan-button">
                  <Barcode className="mr-2 h-4 w-4" /> Barkod Tara
                </Button>
              </div>
              {showResults && (
                <div className="mt-3 overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="product-search-results">
                  {searchResults.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-slate-500">Sonuç bulunamadı</div>
                  ) : searchResults.map((product, index) => (
                    <button
                      key={product.key}
                      type="button"
                      onClick={() => pickSearchResult(product)}
                      className="flex w-full items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 text-left last:border-b-0 hover:bg-blue-50"
                      data-testid={`search-result-${index}`}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-bold text-slate-950">{product.name}</span>
                        <span className="mt-0.5 block text-xs text-slate-500">
                          {product.variant} · {product.sku}{product.barcode ? ` · Barkod: ${product.barcode}` : ""}
                        </span>
                      </span>
                      <span className="shrink-0 text-right">
                        <span className="block text-sm font-bold text-blue-700">{fmtSource(product.sourcePrice, product.sourceCurrency)}</span>
                        <span className={`block text-[11px] font-semibold ${product.available === 0 ? "text-rose-600" : "text-emerald-600"}`}>
                          Stok: {product.available === null ? "—" : qtyFmt(product.available)}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </section>

            <div className="flex gap-2 overflow-x-auto pb-1" data-testid="category-filter-row">
              {categories.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setCategory(item)}
                  className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-150 ${category === item ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-700"}`}
                  data-testid={`category-${slug(item)}`}
                >
                  {item}
                </button>
              ))}
            </div>

            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="cart-panel">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <div>
                  <h2 className="text-lg font-bold text-slate-950">Sepet</h2>
                  <p className="text-xs text-slate-500">Gerçek ürün, varyant, stok, adet ve indirim kontrolü</p>
                </div>
                <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700" data-testid="cart-count">
                  {cart.reduce((sum, item) => sum + item.quantity, 0)} ürün
                </span>
              </div>

              <div className="hidden grid-cols-[minmax(260px,1fr)_190px_150px_120px_130px_48px] gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 md:grid">
                <span>Ürün</span><span className="text-right">Birim Fiyat (TL)</span><span className="text-center">Adet</span><span className="text-center">İndirim</span><span className="text-right">Toplam</span><span />
              </div>

              <div className="divide-y divide-slate-100" data-testid="cart-items-list">
                {cart.length === 0 ? (
                  <div className="flex flex-col items-center justify-center px-6 py-14 text-center" data-testid="cart-empty-state">
                    <ShoppingBasket className="mb-3 h-10 w-10 text-slate-300" />
                    <p className="font-semibold text-slate-700">Sepet boş</p>
                    <p className="mt-1 text-sm text-slate-500">Barkod okutun, ürün arayın veya hızlı ürünlerden seçin.</p>
                  </div>
                ) : cart.map((item) => {
                  const lineTotal = item.unitPrice * item.quantity * (1 - item.discount / 100);
                  const isFx = item.sourceCurrency && item.sourceCurrency !== "TRY";
                  return (
                    <div key={item.key} className="grid grid-cols-1 gap-4 px-4 py-4 md:grid-cols-[minmax(260px,1fr)_190px_150px_120px_130px_48px] md:items-center md:gap-3" data-testid={`cart-item-${item.key}`}>
                      <div className="flex min-w-0 items-center gap-3">
                        <ProductImage src={item.image} alt={item.name} className="h-14 w-14 shrink-0 rounded-lg border border-slate-200 object-cover" />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-bold text-slate-950">{item.name}</div>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{item.variant}</span>
                            <span className="font-mono text-[11px] text-slate-400">{item.sku}</span>
                            {item.barcode && <span className="font-mono text-[11px] text-slate-400">Barkod: {item.barcode}</span>}
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${item.available === 0 ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-700"}`} data-testid={`stock-${item.key}`}>
                              {item.warehouseName || "MAĞAZA"} stok: {item.available === null ? "—" : qtyFmt(item.available)}
                            </span>
                            {item.otherAvailable > 0 && (
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500" data-testid={`other-stock-${item.key}`}>
                                Diğer depolar: {qtyFmt(item.otherAvailable)} (satışta kullanılmaz)
                              </span>
                            )}
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700 hover:bg-blue-100"
                              onClick={() => setHistoryOpenFor({ productId: item.productId, name: item.name, key: item.key })}
                              data-testid={`price-history-${item.key}`}
                            >
                              <History className="h-3 w-3" /> Fiyat Geçmişi
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1 md:text-right" data-testid={`cart-unit-price-${item.key}`}>
                        <Input
                          type="number" step="0.01" min="0"
                          value={item.unitPrice}
                          onChange={(event) => updateUnitPrice(item.key, event.target.value)}
                          disabled={!canOverridePrice}
                          className="h-9 rounded-md border-slate-300 text-right text-sm font-semibold"
                          data-testid={`cart-unit-price-input-${item.key}`}
                        />
                        <div className="text-[10px] font-mono text-slate-500" data-testid={`cart-recommended-${item.key}`}>
                          Tavsiye: {fmtSource(item.sourcePrice, item.sourceCurrency)}
                        </div>
                        {isFx && (
                          <div className="text-[10px] font-mono text-slate-500" data-testid={`cart-try-equiv-${item.key}`}>
                            TL Karşılığı: {money(item.tryPrice)}
                          </div>
                        )}
                        {item.priceEdited && (
                          <div className="text-[10px] font-mono text-amber-600" data-testid={`cart-price-edited-${item.key}`}>
                            Manuel fiyat uygulandı
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 md:justify-center">
                        <Button size="icon" variant="outline" className="h-8 w-8 rounded-md border-slate-300" onClick={() => updateQuantity(item.key, -1)} data-testid={`quantity-decrease-${item.key}`}>
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="w-9 text-center text-sm font-bold" data-testid={`quantity-value-${item.key}`}>{item.quantity}</span>
                        <Button size="icon" variant="outline" className="h-8 w-8 rounded-md border-slate-300" onClick={() => updateQuantity(item.key, 1)} data-testid={`quantity-increase-${item.key}`}>
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="relative md:mx-auto md:w-24">
                        <Percent className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={item.discount}
                          onChange={(event) => updateDiscount(item.key, event.target.value)}
                          className="h-9 rounded-md border-slate-300 pl-8 text-center text-sm"
                          data-testid={`discount-input-${item.key}`}
                        />
                      </div>
                      <div className="text-base font-bold text-slate-950 md:text-right" data-testid={`line-total-${item.key}`}>{money(lineTotal)}</div>
                      <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-400 hover:bg-rose-50 hover:text-rose-600" onClick={() => setCart((current) => current.filter((row) => row.key !== item.key))} data-testid={`remove-item-${item.key}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </section>

            <section data-testid="quick-products-panel">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-950">Hızlı Ürünler</h2>
                  <p className="text-xs text-slate-500">Gerçek ürün ve varyantlar · stok durumuna göre</p>
                </div>
                <span className="text-xs font-semibold text-slate-500" data-testid="quick-product-count">{visibleQuickItems.length} ürün</span>
              </div>
              {loadingProducts ? (
                <div className="flex h-40 items-center justify-center rounded-xl border border-slate-200 bg-white" data-testid="quick-products-loading">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : visibleQuickItems.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-10 text-center text-sm text-slate-500" data-testid="quick-products-empty">
                  Bu kategoride gösterilecek ürün bulunamadı.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {visibleQuickItems.map((product) => (
                    <button
                      key={product.key}
                      type="button"
                      onClick={() => addProduct(product)}
                      className="group overflow-hidden rounded-xl border border-slate-200 bg-white text-left shadow-sm transition-transform duration-150 hover:-translate-y-0.5 hover:border-blue-300"
                      data-testid={`quick-product-${slug(product.sku)}`}
                    >
                      <div className="h-32 overflow-hidden bg-slate-100">
                        <ProductImage src={product.image} alt={product.name} className="h-full w-full object-cover transition-transform duration-150 group-hover:scale-105" />
                      </div>
                      <div className="p-3">
                        <div className="truncate text-sm font-bold text-slate-950">{product.name}</div>
                        <div className="mt-0.5 truncate text-[11px] text-slate-500">{product.variant} · {product.sku}</div>
                        <div className="mt-3 flex items-center justify-between gap-2">
                          <div>
                            <div className="text-sm font-bold text-blue-700">{fmtSource(product.sourcePrice, product.sourceCurrency)}</div>
                            <div className={`mt-0.5 flex items-center gap-1 text-[11px] font-semibold ${product.available === 0 ? "text-rose-600" : "text-emerald-600"}`}>
                              {product.available === 0 && <AlertTriangle className="h-3 w-3" />}
                              Stok: {product.available === null ? "—" : qtyFmt(product.available)}
                            </div>
                          </div>
                          <span className="rounded-full bg-blue-50 p-1.5 text-blue-700 group-hover:bg-blue-600 group-hover:text-white"><PackagePlus className="h-4 w-4" /></span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </div>

          <aside className="2xl:col-span-4">
            <section className="2xl:sticky top-24 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-md" data-testid="sales-summary-panel">
              <div className="border-b border-slate-200 bg-slate-950 px-5 py-4 text-white">
                <h2 className="text-lg font-bold">Satış Özeti</h2>
                <p className="mt-0.5 text-xs text-slate-400">Satış kaydı oluşturulur · stok düşer · fiş no döner</p>
              </div>

              <div className="space-y-5 p-5">
                <div>
                  <label className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500">
                    <UserRound className="h-4 w-4" /> Müşteri
                  </label>
                  <div className="relative">
                    <Input
                      value={customerQuery}
                      onChange={(event) => {
                        setCustomerQuery(event.target.value);
                        setCustomerId("");
                        setCustomerOpen(true);
                      }}
                      onFocus={() => setCustomerOpen(true)}
                      onBlur={() => setTimeout(() => setCustomerOpen(false), 150)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && customerOpen && filteredCustomers.length) {
                          event.preventDefault();
                          const c = filteredCustomers[0];
                          setCustomerId(c.id);
                          setCustomerQuery(c.name);
                          setCustomerOpen(false);
                        }
                      }}
                      placeholder="Müşteri ara: ad, telefon, VKN/TCKN..."
                      className="h-11 rounded-lg border-slate-300 bg-white text-slate-900"
                      data-testid="customer-select"
                      autoComplete="off"
                    />
                    {customerOpen && (
                      <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg" data-testid="customer-search-results">
                        {filteredCustomers.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-slate-500">Müşteri bulunamadı</div>
                        ) : filteredCustomers.slice(0, 20).map((customer) => (
                          <button
                            key={customer.id}
                            type="button"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              setCustomerId(customer.id);
                              setCustomerQuery(customer.name);
                              setCustomerOpen(false);
                            }}
                            className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-blue-50 ${customerId === customer.id ? "bg-blue-50 font-semibold" : ""}`}
                            data-testid={`customer-option-${customer.id}`}
                          >
                            <span className="truncate">{customer.name}</span>
                            <span className="shrink-0 text-xs text-slate-400">{customer.phone || customer.tax_no || ""}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {customers.length === 0 && (
                    <p className="mt-1.5 text-xs text-rose-600" data-testid="customer-required-hint">
                      Satış için en az bir müşteri kaydı gerekli. (Ayarlar / CRM üzerinden ekleyin.)
                    </p>
                  )}
                </div>

                <div className="space-y-2 rounded-lg bg-slate-50 p-4 text-sm">
                  <div className="flex justify-between text-slate-600"><span>Ara Toplam</span><strong data-testid="summary-subtotal">{money(totals.subtotal)}</strong></div>
                  <div className="flex justify-between text-amber-700"><span>İskonto</span><strong data-testid="summary-discount">-{money(totals.discount)}</strong></div>
                  <div className="flex justify-between text-slate-600"><span data-testid="summary-vat-label">KDV{totals.rates.length ? ` (${totals.rates.map((r) => `%${r}`).join(", ")})` : ""}</span><strong data-testid="summary-vat">{money(totals.vat)}</strong></div>
                  <div className="border-t border-slate-200 pt-3">
                    <div className="flex items-end justify-between">
                      <span className="text-sm font-bold text-slate-700">Genel Toplam</span>
                      <span className="text-3xl font-black tracking-tight text-slate-950" data-testid="summary-grand-total">{money(totals.total)}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Ödeme Yöntemi</div>
                  <div className="grid grid-cols-3 gap-2" data-testid="payment-method-group">
                    {PAYMENT_METHODS.map(({ id, label, icon: Icon }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setPaymentMethod(id)}
                        className={`rounded-lg border px-2 py-3 text-xs font-bold transition-colors duration-150 ${paymentMethod === id ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-700"}`}
                        data-testid={`payment-${id}`}
                      >
                        <Icon className="mx-auto mb-1.5 h-5 w-5" />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {paymentMethod === "cash" && (
                  <div className="grid grid-cols-4 gap-2" data-testid="cash-quick-amounts">
                    {[100, 200, 500, 1000].map((amount) => (
                      <button key={amount} type="button" className="rounded-md border border-slate-300 bg-slate-50 py-2 text-xs font-bold text-slate-700 hover:border-emerald-400 hover:text-emerald-700" data-testid={`cash-amount-${amount}`}>
                        ₺{amount.toLocaleString("tr-TR")}
                      </button>
                    ))}
                  </div>
                )}

                <Button
                  className="h-14 w-full rounded-lg bg-emerald-600 text-base font-black tracking-wide text-white hover:bg-emerald-700"
                  onClick={completeSale}
                  disabled={submitting || cart.length === 0 || !(can("orders", "add") || can("wizard", "add"))}
                  title={!(can("orders", "add") || can("wizard", "add")) ? "Satış oluşturma yetkiniz yok" : undefined}
                  data-testid="complete-sale-button"
                >
                  {submitting ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
                  {submitting ? "İŞLENİYOR..." : "SATIŞI TAMAMLA"}
                </Button>

                {lastSale && (
                  <Button
                    variant="outline"
                    className="h-11 w-full rounded-lg border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                    onClick={() => window.print()}
                    data-testid="print-receipt-button"
                  >
                    <Printer className="mr-2 h-4 w-4" /> Fiş Yazdır · {lastSale.orderNo}
                  </Button>
                )}
              </div>
            </section>
          </aside>
        </div>
      </div>

      {/* Mevcut fiyat geçmişi dialog'u reuse — sales / customer_sales / quotes / purchases */}
      <PriceHistoryDialog
        open={!!historyOpenFor}
        onOpenChange={(open) => setHistoryOpenFor(open ? historyOpenFor : null)}
        productId={historyOpenFor?.productId}
        productName={historyOpenFor?.name}
        customerId={customerId}
        onPick={(row) => historyOpenFor && applyHistoricPrice(historyOpenFor.key, row)}
      />

      {/* 80mm termal bilgi fişi — yalnızca yazdırmada görünür (tarayıcı print penceresi) */}
      {lastSale && (
        <>
          <style>{`
            @media print {
              body * { visibility: hidden !important; }
              #qs-receipt, #qs-receipt * { visibility: visible !important; }
              #qs-receipt { display: block !important; position: absolute; left: 0; top: 0; }
              @page { size: 80mm auto; margin: 2mm; }
            }
          `}</style>
          <div
            id="qs-receipt"
            data-testid="qs-receipt"
            style={{ display: "none", width: "72mm", fontFamily: "'Courier New', monospace", fontSize: 11, color: "#000", lineHeight: 1.4 }}
          >
            <div style={{ textAlign: "center" }}>
              {companyInfo?.name && <div style={{ fontWeight: 800, fontSize: 13 }} data-testid="receipt-company-name">{companyInfo.name}</div>}
              {companyInfo?.address && <div>{companyInfo.address}</div>}
              {companyInfo?.phone && <div>Tel: {companyInfo.phone}</div>}
              {(companyInfo?.taxOffice || companyInfo?.taxNo) && (
                <div>{[companyInfo.taxOffice && `V.D.: ${companyInfo.taxOffice}`, companyInfo.taxNo && `VKN: ${companyInfo.taxNo}`].filter(Boolean).join(" · ")}</div>
              )}
            </div>
            <div style={{ borderTop: "1px dashed #000", margin: "6px 0" }} />
            <div style={{ textAlign: "center", fontWeight: 700 }}>SATIŞ BİLGİ FİŞİ</div>
            <div>Fiş No: {lastSale.orderNo}</div>
            <div>Tarih: {new Date(lastSale.date).toLocaleString("tr-TR")}</div>
            {lastSale.customerName && <div>Müşteri: {lastSale.customerName}</div>}
            <div style={{ borderTop: "1px dashed #000", margin: "6px 0" }} />
            {lastSale.items.map((item, idx) => (
              <div key={idx} style={{ marginBottom: 4 }}>
                <div style={{ fontWeight: 700 }}>{item.name}</div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{item.variant !== "Standart" ? `${item.variant} · ` : ""}{item.quantity} x {money(item.price)}</span>
                  <span>{money(item.lineTotal)}</span>
                </div>
                {item.discount > 0 && <div>İndirim: %{item.discount}</div>}
              </div>
            ))}
            <div style={{ borderTop: "1px dashed #000", margin: "6px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between" }}><span>Ara Toplam</span><span>{money(lastSale.totals.subtotal)}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between" }}><span>İskonto</span><span>-{money(lastSale.totals.discount)}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between" }}><span>KDV</span><span>{money(lastSale.totals.vat)}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: 13, marginTop: 2 }}><span>GENEL TOPLAM</span><span>{money(lastSale.totals.total)}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}><span>Ödeme</span><span>{lastSale.paymentLabel}</span></div>
            <div style={{ borderTop: "1px dashed #000", margin: "6px 0" }} />
            <div style={{ textAlign: "center", fontSize: 10 }} data-testid="receipt-disclaimer">
              Bu belge satış/ödeme bilgi fişidir.<br />Mali belge yerine geçmez.
            </div>
          </div>
        </>
      )}
      <BarcodeScanner open={scanOpen} onOpenChange={setScanOpen} onDetected={onScanned} />
      <UnknownBarcodeDialog code={unknownCode} onClose={() => setUnknownCode("")}
        onAssigned={(r) => { const c = unknownCode; setUnknownCode(""); searchProducts(r.variant ? c : r.product.name, true); }} />
    </div>
  );
}
